import { PolfEventPokerStatusEnum } from '@polf/shared/codegen/types'
import { formatNOK } from '@polf/shared/locale'

import { payoutAmountsNOK } from '../../../util/payouts'
import Clock from '../components/Clock'
import PayoutList from '../components/PayoutList'
import StandingsBoard from '../components/StandingsBoard'
import type { PokerView } from '../hooks/usePokerView'

const toDate = (s: string | null | undefined): Date | null =>
  s ? new Date(s) : null

/**
 * Player has been eliminated, OR has won (variant='winner').
 *
 * This isn't a sticky-on-top patch any more — it's the page identity.
 * The hero replaces SeatCard; live tournament data follows (clock +
 * standings) so the player can spectate.
 *
 * Premium UX consideration: the moment of elimination is emotional. The
 * page should acknowledge the result with payout (if any) and the
 * players outlasted (a small dignity), THEN pivot to spectator mode.
 *
 * Winner variant: crown + "Du vant!" hero, payout, players outlasted,
 * full standings. No clock (tournament is finished by definition).
 */
const EliminatedView = ({
  view,
  variant,
  playerKey,
}: {
  view: PokerView
  variant: 'eliminated' | 'winner'
  playerKey: string
}) => {
  const seat = view.seat!
  const position = seat.finishingPosition ?? null
  const totalPlayers = view.playersTotal ?? null
  // "Players outlasted" — a finishing position of N means you outlasted
  // (totalPlayers - N) players. Only meaningful when both numbers known.
  const outlasted =
    position != null && totalPlayers != null && totalPlayers > position
      ? totalPlayers - position
      : null

  const krMap =
    view.usePayouts && view.prizePool
      ? payoutAmountsNOK(view.payouts, view.prizePool)
      : null
  const payoutKr = position != null ? krMap?.get(position) ?? null : null

  const isWinner = variant === 'winner'
  const tournamentOver =
    view.status === PolfEventPokerStatusEnum.Finished || isWinner
  // Clock stays visible only while the tournament is still running. Once
  // finished (winner included), nothing to count down.
  const clockVisible = view.currentLevel && !tournamentOver

  return (
    <section className="pb-4">
      <div className="px-4 pt-4 pb-2">
        <h1 className="font-display text-xl font-bold text-green-900">
          {isWinner ? 'Mester' : 'Resultat'}
        </h1>
      </div>

      {/* Hero — winner is gold, eliminated is paper. Big position numeral,
          payout in NOK, dignity line for "outlasted N". */}
      <div
        className={`mx-2 my-3 rounded-lg p-6 shadow-lg flex flex-col items-center gap-3 ${
          isWinner ? 'text-ink-900' : 'text-paper'
        }`}
        style={{
          background: isWinner
            ? 'linear-gradient(180deg, #f5d76e 0%, #d4a017 100%)'
            : 'linear-gradient(180deg, #0e2418 0%, #050d09 100%)',
        }}
      >
        <div
          className={`text-[11px] up ${
            isWinner ? 'text-ink-700' : 'text-green-100/80'
          }`}
        >
          {isWinner ? 'Du vant turneringen' : 'Du endte på'}
        </div>
        {position != null ? (
          <div className="flex items-baseline gap-2">
            <span
              className="font-display font-extrabold num leading-none"
              style={{ fontSize: 88, letterSpacing: '-0.04em' }}
            >
              {position}
              <span className="text-[40px] opacity-70">.</span>
            </span>
            <span className="font-display font-bold text-2xl opacity-80">
              {isWinner ? '🏆' : 'plass'}
            </span>
          </div>
        ) : (
          <div className="font-display font-bold text-2xl">Slått ut</div>
        )}
        {payoutKr != null ? (
          <div
            className={`text-base ${
              isWinner ? 'text-ink-700' : 'text-green-100/80'
            }`}
          >
            Premie{' '}
            <span
              className={`font-mono num font-bold ${
                isWinner ? 'text-ink-900' : 'text-gold'
              }`}
            >
              {formatNOK(payoutKr)}
            </span>
          </div>
        ) : null}
        {outlasted != null && outlasted > 0 ? (
          <div
            className={`text-[11px] ${
              isWinner ? 'text-ink-700/80' : 'text-green-100/70'
            }`}
          >
            Du slo {outlasted} spillere
          </div>
        ) : null}
      </div>

      {/* Spectator strip — live clock while the tournament continues, so
          you can follow remaining levels. Hidden once finished. */}
      {clockVisible ? (
        <Clock
          level={view.currentLevel!}
          nextLevel={view.nextLevel ?? null}
          inBreak={view.currentInBreak ?? false}
          startedAt={toDate(view.currentLevelStartedAt)}
          pausedAt={toDate(view.currentLevelPausedAt)}
          levelIndex={view.currentLevelIndex ?? 0}
          status={view.status!}
          playersLeft={view.playersLeft}
          playersTotal={view.playersTotal}
          averageStack={view.averageStack}
        />
      ) : null}

      <StandingsBoard
        active={view.activeStandings}
        eliminated={view.eliminations}
        yourSeatId={view.seat?.id}
        isFinished={tournamentOver}
        playerKey={playerKey}
      />

      {view.usePayouts && view.payouts.length > 0 ? (
        <PayoutList
          payouts={view.payouts}
          prizePool={view.prizePool}
          highlightPosition={position}
        />
      ) : null}
    </section>
  )
}

export default EliminatedView
